<template>
  <div id="AdminMap" class="container-fluid py-4">
    <div
      class="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2"
    >
      <div>
        <h1 class="mb-1">Device Map</h1>
        <p class="text-body-tertiary mb-0">
          {{ visibleStats.deviceCount.toLocaleString() }} devices ·
          {{ visibleStats.totalPowerLabel }} total capacity
          <span v-if="devicesMissingCoordinates > 0">
            · {{ devicesMissingCoordinates.toLocaleString() }} awaiting
            geocoding
          </span>
        </p>
      </div>
      <div class="d-flex align-items-center gap-2 flex-wrap">
        <div style="width: 220px">
          <MultiSelectComponent
            v-model="selectedCountries"
            :options="countryOptions"
            placeholder="All countries"
            size="sm"
          />
        </div>
        <ActionButtonWithSpinner
          variant="btn-outline-primary"
          icon="globe"
          :busy="isGeocoding"
          @click="startGeocoding"
        >
          Geocode missing locations
        </ActionButtonWithSpinner>
      </div>
    </div>

    <LoadingIndicatorComponent v-if="isLoading" />

    <div v-show="!isLoading" class="position-relative">
      <div ref="mapContainerRef" class="device-map rounded border" />

      <!-- Technology legend: click chips to toggle technologies on/off -->
      <div class="device-map-legend rounded border shadow-sm">
        <button
          v-for="tech in legendItems"
          :key="tech.key"
          type="button"
          class="device-map-legend__item"
          :class="{ 'device-map-legend__item--off': hiddenTechs.has(tech.key) }"
          :title="`${tech.deviceCount} devices · ${tech.powerLabel}. Click to toggle.`"
          @click="toggleTech(tech.key)"
        >
          <span
            class="device-map-legend__dot"
            :style="{ backgroundColor: tech.color }"
          />
          <span class="device-map-legend__label">{{ tech.name }}</span>
          <span class="device-map-legend__count">{{ tech.deviceCount }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import ActionButtonWithSpinner from "@/components/common/ActionButtonWithSpinner.vue";
import LoadingIndicatorComponent from "@/components/common/LoadingIndicatorComponent.vue";
import MultiSelectComponent from "@/components/common/MultiSelectComponent.vue";
import { useBusy } from "@/composables/useBusyAction";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import { goSolidApi } from "@/composables/useGoSolidApi";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster";
import "leaflet.markercluster/dist/MarkerCluster.css";
import {
  computed,
  onBeforeUnmount,
  onMounted,
  ref,
  useTemplateRef,
  watch,
} from "vue";
import {
  devicePopupHtml,
  formatPower,
  markerRadius,
  technologyColorFromCode,
  technologyKeyFromCode,
  technologyNameFromCode,
  UNKNOWN_TECH_COLOR,
  UNKNOWN_TECH_KEY,
  type DeviceMapMarker,
} from "./map/deviceMapUtils";

defineOptions({
  name: "AdminMapView",
});

const { globalErrorHandler, globalSuccessHandler } = useGlobalHandlers();

const devices = ref<DeviceMapMarker[]>([]);
const devicesMissingCoordinates = ref(0);
const selectedCountries = ref<string[]>([]);
const hiddenTechs = ref<Set<string>>(new Set());
const isLoading = ref(true);
const { busy: isGeocoding, run: runGeocoding } = useBusy();

const mapContainerRef = useTemplateRef<HTMLElement>("mapContainerRef");
let map: L.Map | null = null;
let clusterGroup: L.MarkerClusterGroup | null = null;
let hasFitInitialBounds = false;

const countryOptions = computed(() => {
  const countries = new Set<string>();
  for (const device of devices.value) {
    if (device.country) countries.add(device.country);
  }
  return [...countries].sort().map((c) => ({ label: c, value: c }));
});

const countryFilteredDevices = computed(() => {
  if (selectedCountries.value.length === 0) return devices.value;
  return devices.value.filter(
    (d) => d.country && selectedCountries.value.includes(d.country)
  );
});

const visibleDevices = computed(() =>
  countryFilteredDevices.value.filter(
    (d) => !hiddenTechs.value.has(technologyKeyFromCode(d.technology_code))
  )
);

// Legend reflects the country filter (not the tech toggles) so toggled-off
// technologies stay visible in the legend and can be re-enabled.
const legendItems = computed(() => {
  const byTech = new Map<
    string,
    { deviceCount: number; powerKw: number; sampleCode: string | null }
  >();
  for (const device of countryFilteredDevices.value) {
    const key = technologyKeyFromCode(device.technology_code);
    const entry = byTech.get(key) ?? {
      deviceCount: 0,
      powerKw: 0,
      sampleCode: device.technology_code,
    };
    entry.deviceCount += 1;
    entry.powerKw += device.device_power ?? 0;
    byTech.set(key, entry);
  }
  return [...byTech.entries()]
    .sort((a, b) => b[1].powerKw - a[1].powerKw)
    .map(([key, { deviceCount, powerKw, sampleCode }]) => ({
      key,
      name:
        key === UNKNOWN_TECH_KEY ? "Other" : technologyNameFromCode(sampleCode),
      color:
        key === UNKNOWN_TECH_KEY
          ? UNKNOWN_TECH_COLOR
          : technologyColorFromCode(sampleCode),
      deviceCount,
      powerLabel: formatPower(powerKw),
    }));
});

const visibleStats = computed(() => {
  const totalKw = visibleDevices.value.reduce(
    (sum, d) => sum + (d.device_power ?? 0),
    0
  );
  return {
    deviceCount: visibleDevices.value.length,
    totalPowerLabel: formatPower(totalKw),
  };
});

const toggleTech = (key: string) => {
  const next = new Set(hiddenTechs.value);
  if (next.has(key)) {
    next.delete(key);
  } else {
    next.add(key);
  }
  hiddenTechs.value = next;
};

// Cluster icon: donut segmented by technology share, sized by device count.
const buildClusterIcon = (cluster: L.MarkerCluster): L.DivIcon => {
  const children =
    cluster.getAllChildMarkers() as unknown as (L.CircleMarker & {
      deviceData?: DeviceMapMarker;
    })[];
  const byColor = new Map<string, number>();
  for (const child of children) {
    const color = technologyColorFromCode(
      child.deviceData?.technology_code ?? null
    );
    byColor.set(color, (byColor.get(color) ?? 0) + 1);
  }

  const count = children.length;
  const size = count < 10 ? 34 : count < 100 ? 42 : 50;
  const strokeWidth = 5;
  const radius = size / 2 - strokeWidth / 2;
  const circumference = 2 * Math.PI * radius;

  let offset = 0;
  const segments = [...byColor.entries()]
    .sort((a, b) => b[1] - a[1])
    .map(([color, n]) => {
      const fraction = n / count;
      const segment = `<circle r="${radius}" cx="${size / 2}" cy="${size / 2}"
        fill="none" stroke="${color}" stroke-width="${strokeWidth}"
        stroke-dasharray="${fraction * circumference} ${circumference}"
        stroke-dashoffset="${-offset * circumference}"
        transform="rotate(-90 ${size / 2} ${size / 2})" />`;
      offset += fraction;
      return segment;
    })
    .join("");

  return L.divIcon({
    html: `<svg width="${size}" height="${size}" class="device-map-cluster">
        <circle r="${radius}" cx="${size / 2}" cy="${size / 2}" class="device-map-cluster__bg" />
        ${segments}
        <text x="50%" y="50%" text-anchor="middle" dy="0.35em" class="device-map-cluster__count">${count}</text>
      </svg>`,
    className: "device-map-cluster-icon",
    iconSize: [size, size],
  });
};

const renderMarkers = () => {
  if (!map) return;

  // Recreate the cluster group instead of clearLayers(): with chunkedLoading,
  // an in-flight addLayers from the previous filter state would otherwise keep
  // appending stale markers after the clear.
  if (clusterGroup) {
    map.removeLayer(clusterGroup);
  }
  clusterGroup = L.markerClusterGroup({
    maxClusterRadius: 45,
    spiderfyOnMaxZoom: true,
    disableClusteringAtZoom: 13,
    showCoverageOnHover: false,
    chunkedLoading: true,
    iconCreateFunction: buildClusterIcon,
  });
  map.addLayer(clusterGroup);

  const markers: L.Layer[] = [];
  const bounds: L.LatLngTuple[] = [];
  for (const device of visibleDevices.value) {
    const color = technologyColorFromCode(device.technology_code);
    const marker = L.circleMarker([device.lat, device.lng], {
      radius: markerRadius(device.device_power),
      color: "#ffffff",
      weight: 1.5,
      fillColor: color,
      fillOpacity: 0.85,
    }).bindPopup(devicePopupHtml(device), { maxWidth: 320 });
    (marker as L.CircleMarker & { deviceData: DeviceMapMarker }).deviceData =
      device;
    markers.push(marker);
    bounds.push([device.lat, device.lng]);
  }
  // Bulk insert: addLayers reclusters once (vs. once per marker with addLayer),
  // and chunkedLoading on the group keeps the UI responsive for large sets.
  clusterGroup.addLayers(markers);

  // Fit once on load and again when the country filter changes; tech toggles
  // keep the current viewport so the map doesn't jump under the user.
  if (bounds.length > 0 && !hasFitInitialBounds) {
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 10 });
    hasFitInitialBounds = true;
  }
};

const refitToVisible = () => {
  if (!map || visibleDevices.value.length === 0) return;
  map.fitBounds(
    visibleDevices.value.map((d) => [d.lat, d.lng] as L.LatLngTuple),
    { padding: [40, 40], maxZoom: 10 }
  );
};

const fetchMapData = async () => {
  isLoading.value = true;
  try {
    const response = await goSolidApi.get("/api/admin/map-data");
    devices.value = response.data.devices;
    devicesMissingCoordinates.value = response.data.missing_coordinates;
  } catch (error: unknown) {
    globalErrorHandler(error);
  } finally {
    isLoading.value = false;
  }
};

const startGeocoding = () =>
  runGeocoding(async () => {
    try {
      await goSolidApi.post("/api/admin/map-data/geocode");
      globalSuccessHandler(
        "Geocoding started",
        "Locations are being resolved in the background (up to ~5 minutes per 300 devices). Reload the page later to see new markers."
      );
    } catch (error: unknown) {
      globalErrorHandler(error);
    }
  });

onMounted(async () => {
  await fetchMapData();
  if (!mapContainerRef.value) return;

  map = L.map(mapContainerRef.value).setView([58.38, 26.74], 6);
  L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
    maxZoom: 19,
    attribution:
      '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
  }).addTo(map);

  renderMarkers();
});

onBeforeUnmount(() => {
  map?.remove();
  map = null;
  clusterGroup = null;
});

watch(visibleDevices, renderMarkers);
watch(selectedCountries, refitToVisible);
</script>

<style scoped lang="scss">
.device-map {
  height: calc(100vh - 220px);
  min-height: 500px;
  z-index: 0;
}

.device-map-legend {
  position: absolute;
  bottom: 1.5rem;
  left: 0.75rem;
  z-index: 800; // above Leaflet panes (max ~700), below its modals
  background: var(--phoenix-body-bg);
  padding: 0.375rem;
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  max-width: 12rem;

  &__item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    border: 0;
    background: transparent;
    padding: 0.25rem 0.5rem;
    border-radius: 0.25rem;
    font-size: 0.8125rem;
    color: var(--phoenix-body-color);
    text-align: left;

    &:hover {
      background: var(--phoenix-body-highlight-bg);
    }

    &--off {
      opacity: 0.4;

      .device-map-legend__dot {
        background-color: transparent !important;
        box-shadow: inset 0 0 0 1.5px var(--phoenix-secondary-color);
      }
    }
  }

  &__dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    flex-shrink: 0;
  }

  &__label {
    flex-grow: 1;
  }

  &__count {
    color: var(--phoenix-body-tertiary-color);
    font-variant-numeric: tabular-nums;
  }
}

:deep(.device-map-cluster-icon) {
  background: transparent;
  border: 0;
}

:deep(.device-map-cluster) {
  filter: drop-shadow(0 1px 3px rgba(0, 0, 0, 0.3));

  .device-map-cluster__bg {
    fill: var(--phoenix-body-bg, #fff);
  }

  .device-map-cluster__count {
    font-size: 12px;
    font-weight: 600;
    fill: var(--phoenix-body-color, #333);
  }
}

:deep(.device-map-popup) {
  font-size: 0.875rem;

  .device-map-popup__title {
    font-weight: 600;
    margin-bottom: 0.25rem;
  }

  .device-map-popup__tech {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    margin-bottom: 0.375rem;
    font-weight: 500;
  }

  .device-map-popup__dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    display: inline-block;
    flex-shrink: 0;
  }

  .device-map-popup__row {
    margin-bottom: 0.125rem;
  }
}
</style>
